import {
  ProcessedImage,
  ProcessedFile,
  ProcessedGeometry,
  PointCloudData,
} from "../state/types";
import distance from "@turf/distance";
import * as turf from "@turf/helpers";
import center from "@turf/center";
import { utcToZonedTime, format as formatTz } from "date-fns-tz";

export type ValidationCode =
  | "NO_FILES"
  | "INSUFFICIENT_IMAGES"
  | "MISSING_FILE"
  | "PROCESSING_ERROR"
  | "GEOMETRY_ONLY"
  | "MISSING_PRJ"
  | "MISSING_SHX"
  | "MISSING_DBF"
  | "MISSING_TIMEZONE"
  | "INVALID_LATITUDE"
  | "INVALID_LONGITUDE"
  | "MISSING_TIME_RANGE"
  | "SAME_TIME_RANGE"
  | "REVERSED_TIME_RANGE"
  | "MISSING_GPS"
  | "MISSING_TIMESTAMPS"
  | "LONG_DATE_RANGE"
  | "FUTURE_START_TIME"
  | "FUTURE_END_TIME"
  | "FILE_AGE"
  | "DISTANCE_FROM_SITE";

export interface ValidationIssue {
  severity: "error" | "warning";
  category: "files" | "geometry" | "form" | "data_quality";
  message: string;
  code?: ValidationCode;
  fileId?: string;
  field?: string;
}

export interface ValidationResult {
  valid: boolean;
  errors: ValidationIssue[];
  warnings: ValidationIssue[];
}

export interface TimeRangeValidationResult {
  isValid: boolean;
  errorType: "missing" | "same" | "reversed" | null;
}

interface ValidationContext {
  imageUploads: ProcessedImage[];
  lidarUploads: ProcessedFile<PointCloudData>[];
  geometryUploads: ProcessedGeometry[];
  timezone: string | null;
  latitude: string;
  longitude: string;
  startTime: string;
  endTime: string;
  fileAgeMap?: Map<string, boolean>;
  siteLocation?: { longitude: number; latitude: number } | null;
}

export const FILE_AGE_WARNING_DAYS = 7;
export const SITE_DISTANCE_WARNING_KM = 1;

export function parseExifDate(
  value: string | Date | undefined | null
): Date | null {
  if (!value) return null;
  if (value instanceof Date) {
    return isNaN(value.getTime()) ? null : value;
  }
  // ISO / already-parseable strings
  let date = new Date(value);
  if (!isNaN(date.getTime())) return date;
  // EXIF datetimes are conventionally "YYYY:MM:DD HH:MM:SS" — normalize the
  // date portion's colons to dashes and retry (same idea as getDateList in
  // src/utils/index.ts).
  const [datePart, timePart] = value.split(" ");
  if (datePart && timePart) {
    date = new Date(`${datePart.replaceAll(":", "-")}T${timePart}`);
    if (!isNaN(date.getTime())) return date;
  }
  return null;
}

// Capture time for display: EXIF DateTimeOriginal projected into the survey
// timezone, matching how ReviewForm renders Start/End. Falls back to the file's
// modified time when the image has no EXIF capture time.
export function formatCaptureTimeForDisplay(
  image: ProcessedImage,
  timezone: string | null
): { date: string; time: string } {
  const exifDate = parseExifDate(image.data?.exif?.dateTime);
  const ms = image.file.lastModified;
  const source = exifDate ?? (ms ? new Date(ms) : null);
  if (!source) {
    return { date: "—", time: "" };
  }
  const tz = timezone ?? "Etc/UTC";
  const zoned = utcToZonedTime(source.toISOString(), tz);
  return {
    date: formatTz(zoned, "yyyy-MM-dd", { timeZone: tz }),
    time: formatTz(zoned, "h:mm:ss a zzz", { timeZone: tz }),
  };
}

export function getFileCaptureTime(
  upload: ProcessedImage | ProcessedFile<PointCloudData>
): number | null {
  if (upload.data && "exif" in upload.data) {
    const imageUpload = upload as ProcessedImage;
    const captured = parseExifDate(imageUpload.data?.exif?.dateTime);
    return captured ? captured.getTime() : (imageUpload.file?.lastModified ?? null);
  }
  const lidarUpload = upload as ProcessedFile<PointCloudData>;
  if (lidarUpload?.data?.timestamps) {
    return lidarUpload.data.timestamps[0];
  }
  return null;
}

export function isFileOld(
  captureTime: number | null,
  thresholdDays = FILE_AGE_WARNING_DAYS
): boolean {
  if (!captureTime) return false;
  const thresholdMs = thresholdDays * 24 * 60 * 60 * 1000;
  return Date.now() - captureTime > thresholdMs;
}

export function getFileAgeInDays(captureTime: number | null): number | null {
  if (!captureTime) return null;
  const ageMs = Date.now() - captureTime;
  return Math.floor(ageMs / (24 * 60 * 60 * 1000));
}

export function validateTimeRange(
  startTime: string | null,
  endTime: string | null
): TimeRangeValidationResult {
  if (
    !startTime ||
    startTime.trim().length === 0 ||
    !endTime ||
    endTime.trim().length === 0
  ) {
    return {
      isValid: false,
      errorType: "missing",
    };
  }

  const startDate = new Date(startTime);
  const endDate = new Date(endTime);

  if (startDate >= endDate) {
    if (startDate.getTime() === endDate.getTime()) {
      return {
        isValid: false,
        errorType: "same",
      };
    } else {
      return {
        isValid: false,
        errorType: "reversed",
      };
    }
  }

  return {
    isValid: true,
    errorType: null,
  };
}

function validateFiles(context: ValidationContext): ValidationIssue[] {
  const issues: ValidationIssue[] = [];
  const { imageUploads, lidarUploads, geometryUploads } = context;

  if (
    imageUploads.length === 0 &&
    lidarUploads.length === 0 &&
    geometryUploads.length === 0
  ) {
    issues.push({
      severity: "error",
      category: "files",
      code: "NO_FILES",
      message:
        "No files added to order. Please add at least one image, point cloud, or geometry file.",
    });
  }

  if (imageUploads.length > 0 && imageUploads.length < 6) {
    issues.push({
      severity: "error",
      category: "files",
      code: "INSUFFICIENT_IMAGES",
      message: `At least 6 images are required. Currently have ${
        imageUploads.length
      } image${imageUploads.length === 1 ? "" : "s"}.`,
    });
  }

  imageUploads.forEach((upload) => {
    if (upload.missing) {
      issues.push({
        severity: "error",
        category: "files",
        code: "MISSING_FILE",
        message: `Image file "${upload.file.name}" is missing. Please re-add this file.`,
        fileId: upload.id,
      });
    }
  });

  lidarUploads.forEach((upload) => {
    if (upload.missing) {
      issues.push({
        severity: "error",
        category: "files",
        code: "MISSING_FILE",
        message: `Point cloud file "${upload.file.name}" is missing. Please re-add this file.`,
        fileId: upload.id,
      });
    }
  });

  geometryUploads.forEach((upload) => {
    if (upload.missing) {
      issues.push({
        severity: "error",
        category: "files",
        code: "MISSING_FILE",
        message: `Geometry file "${upload.name}" is missing. Please re-add this file.`,
        fileId: upload.id,
      });
    }
  });

  imageUploads.forEach((upload) => {
    if (upload.error) {
      issues.push({
        severity: "error",
        category: "files",
        code: "PROCESSING_ERROR",
        message: `Failed to process image "${upload.file.name}": ${upload.error}`,
        fileId: upload.id,
      });
    }
  });

  lidarUploads.forEach((upload) => {
    if (upload.error) {
      issues.push({
        severity: "error",
        category: "files",
        code: "PROCESSING_ERROR",
        message: `Failed to process point cloud "${upload.file.name}": ${upload.error}`,
        fileId: upload.id,
      });
    }
  });

  geometryUploads.forEach((upload) => {
    if (upload.error) {
      issues.push({
        severity: "error",
        category: "files",
        code: "PROCESSING_ERROR",
        message: `Failed to process geometry "${upload.name}": ${upload.error}`,
        fileId: upload.id,
      });
    }
  });

  geometryUploads.forEach((upload) => {
    if (upload.progress < 1 && !upload.error) {
      issues.push({
        severity: "error",
        category: "files",
        code: "PROCESSING_ERROR",
        message: `Geometry "${upload.name}" is still being processed. Please wait for processing to complete.`,
        fileId: upload.id,
      });
    }
  });

  return issues;
}

function validateGeometry(context: ValidationContext): ValidationIssue[] {
  const issues: ValidationIssue[] = [];
  const { imageUploads, lidarUploads, geometryUploads } = context;

  if (
    geometryUploads.length > 0 &&
    imageUploads.length === 0 &&
    lidarUploads.length === 0
  ) {
    issues.push({
      severity: "error",
      category: "geometry",
      code: "GEOMETRY_ONLY",
      message:
        "Cannot submit order with only geometry files. Please add at least one image or point cloud file.",
    });
  }

  geometryUploads.forEach((upload) => {
    if (upload.type === "shp") {
      if (!upload.files.prj) {
        issues.push({
          severity: "error",
          category: "geometry",
          code: "MISSING_PRJ",
          message: `Shapefile "${upload.name}" is missing the required .prj projection file.`,
          fileId: upload.id,
        });
      }
    }
  });

  return issues;
}

function validateForm(context: ValidationContext): ValidationIssue[] {
  const issues: ValidationIssue[] = [];
  const { timezone, latitude, longitude, startTime, endTime } = context;

  if (timezone === null || timezone.trim().length === 0) {
    issues.push({
      severity: "error",
      category: "form",
      code: "MISSING_TIMEZONE",
      message: "Timezone is required for proper timestamp interpretation.",
      field: "timezone",
    });
  }

  const latNum = parseFloat(latitude);

  if (isNaN(latNum) || latNum < -90 || latNum > 90) {
    issues.push({
      severity: "error",
      category: "form",
      code: "INVALID_LATITUDE",
      message: "Latitude must be a valid number between -90 and 90.",
      field: "latitude",
    });
  }

  const lonNum = parseFloat(longitude);

  if (isNaN(lonNum) || lonNum < -180 || lonNum > 180) {
    issues.push({
      severity: "error",
      category: "form",
      code: "INVALID_LONGITUDE",
      message: "Longitude must be a valid number between -180 and 180.",
      field: "longitude",
    });
  }

  const timeRangeValidation = validateTimeRange(startTime, endTime);
  if (!timeRangeValidation.isValid) {
    switch (timeRangeValidation.errorType) {
      case "missing":
        issues.push({
          severity: "error",
          category: "form",
          code: "MISSING_TIME_RANGE",
          message: "Start time and end time are required for order metadata.",
          field: "startTime",
        });
        break;
      case "same":
        issues.push({
          severity: "error",
          category: "form",
          code: "SAME_TIME_RANGE",
          message: "Start time and end time cannot be the same.",
          field: "startTime",
        });
        break;
      case "reversed":
        issues.push({
          severity: "error",
          category: "form",
          code: "REVERSED_TIME_RANGE",
          message: "Start time must be before end time.",
          field: "startTime",
        });
        break;
    }
  }

  return issues;
}

function validateDataQuality(context: ValidationContext): ValidationIssue[] {
  const issues: ValidationIssue[] = [];
  const { imageUploads, lidarUploads, startTime, endTime } = context;

  imageUploads.forEach((upload) => {
    if (upload.data && !upload.data.exif?.geoLocation) {
      issues.push({
        severity: "warning",
        category: "data_quality",
        code: "MISSING_GPS",
        message: `Image "${upload.file.name}" has no GPS location data. This may affect processing accuracy.`,
        fileId: upload.id,
      });
    }
  });

  lidarUploads.forEach((upload) => {
    if (upload.data && !upload.data.timestamps) {
      issues.push({
        severity: "warning",
        category: "data_quality",
        code: "MISSING_TIMESTAMPS",
        message: `LAS 1.2 file "${upload.file.name}" does not contain timestamp data. Please confirm your start and end times are correct.`,
        fileId: upload.id,
      });
    }
  });

  if (startTime && endTime) {
    const start = new Date(startTime);
    const end = new Date(endTime);
    const daysDiff = (end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24);

    if (daysDiff > 30) {
      issues.push({
        severity: "warning",
        category: "data_quality",
        code: "LONG_DATE_RANGE",
        message: `Capture time range spans ${Math.round(
          daysDiff
        )} days, which is unusually long. Please verify the dates are correct.`,
        field: "dateRange",
      });
    }
  }

  if (startTime) {
    const start = new Date(startTime);
    const now = new Date();
    if (start > now) {
      issues.push({
        severity: "warning",
        category: "data_quality",
        code: "FUTURE_START_TIME",
        message:
          "Start time is in the future. Please verify the capture date is correct.",
        field: "startTime",
      });
    }
  }

  if (endTime) {
    const end = new Date(endTime);
    const now = new Date();
    if (end > now) {
      issues.push({
        severity: "warning",
        category: "data_quality",
        code: "FUTURE_END_TIME",
        message:
          "End time is in the future. Please verify the capture date is correct.",
        field: "endTime",
      });
    }
  }

  return issues;
}

function validateFileAge(context: ValidationContext): ValidationIssue[] {
  const issues: ValidationIssue[] = [];
  const { imageUploads, lidarUploads, fileAgeMap } = context;

  const oldImageAges: number[] = [];
  const oldLidarAges: number[] = [];

  imageUploads.forEach((upload) => {
    const isOld =
      fileAgeMap?.get(upload.id) ?? isFileOld(getFileCaptureTime(upload));

    if (isOld) {
      const captureTime = getFileCaptureTime(upload);
      const ageInDays = getFileAgeInDays(captureTime);
      if (ageInDays !== null) {
        oldImageAges.push(ageInDays);
      }
    }
  });

  lidarUploads.forEach((upload) => {
    const isOld =
      fileAgeMap?.get(upload.id) ?? isFileOld(getFileCaptureTime(upload));

    if (isOld) {
      const captureTime = getFileCaptureTime(upload);
      const ageInDays = getFileAgeInDays(captureTime);
      if (ageInDays !== null) {
        oldLidarAges.push(ageInDays);
      }
    }
  });

  const parts: string[] = [];

  if (oldImageAges.length > 0) {
    const totalImages = imageUploads.length;
    const minAge = Math.min(...oldImageAges);
    const maxAge = Math.max(...oldImageAges);
    const ageRange =
      minAge === maxAge ? `${minAge} days old` : `${minAge}-${maxAge} days old`;

    if (oldImageAges.length === 1 && totalImages === 1) {
      parts.push(
        `The image is ${ageRange} (older than ${FILE_AGE_WARNING_DAYS} days)`
      );
    } else if (oldImageAges.length === totalImages) {
      parts.push(
        `All ${totalImages} images are ${ageRange} (older than ${FILE_AGE_WARNING_DAYS} days)`
      );
    } else {
      parts.push(
        `${oldImageAges.length} of ${totalImages} image${
          totalImages > 1 ? "s" : ""
        } ${
          oldImageAges.length > 1 ? "are" : "is"
        } ${ageRange} (older than ${FILE_AGE_WARNING_DAYS} days)`
      );
    }
  }

  if (oldLidarAges.length > 0) {
    const totalLidar = lidarUploads.length;
    const minAge = Math.min(...oldLidarAges);
    const maxAge = Math.max(...oldLidarAges);
    const ageRange =
      minAge === maxAge ? `${minAge} days old` : `${minAge}-${maxAge} days old`;

    if (oldLidarAges.length === 1 && totalLidar === 1) {
      parts.push(
        `The point cloud is ${ageRange} (older than ${FILE_AGE_WARNING_DAYS} days)`
      );
    } else if (oldLidarAges.length === totalLidar) {
      parts.push(
        `All ${totalLidar} point clouds are ${ageRange} (older than ${FILE_AGE_WARNING_DAYS} days)`
      );
    } else {
      parts.push(
        `${oldLidarAges.length} of ${totalLidar} point cloud${
          totalLidar > 1 ? "s" : ""
        } ${
          oldLidarAges.length > 1 ? "are" : "is"
        } ${ageRange} (older than ${FILE_AGE_WARNING_DAYS} days)`
      );
    }
  }

  if (parts.length > 0) {
    issues.push({
      severity: "warning",
      category: "data_quality",
      code: "FILE_AGE",
      message: `${parts.join(
        " and "
      )}. Please verify the capture data is correct.`,
    });
  }

  return issues;
}

function validateDistanceFromSite(
  context: ValidationContext
): ValidationIssue[] {
  const issues: ValidationIssue[] = [];
  const { imageUploads, lidarUploads, siteLocation } = context;

  if (!siteLocation) {
    return issues;
  }

  const imagesWithGPS = imageUploads.filter(
    (img) => img.data?.exif?.geoLocation
  );

  const lidarWithBbox = lidarUploads.filter((lidar) => lidar.data?.bbox);

  if (imagesWithGPS.length === 0 && lidarWithBbox.length === 0) {
    return issues;
  }

  const points: turf.Feature<turf.Point>[] = [];

  imagesWithGPS.forEach((img) => {
    const geoLocation = img.data.exif.geoLocation;
    if (geoLocation) {
      points.push(turf.point([geoLocation.longitude, geoLocation.latitude]));
    }
  });

  lidarWithBbox.forEach((lidar) => {
    const bbox = lidar.data.bbox;
    if (bbox && bbox.length >= 6) {
      const centerX = (bbox[0] + bbox[3]) / 2;
      const centerY = (bbox[1] + bbox[4]) / 2;
      points.push(turf.point([centerX, centerY]));
    }
  });

  if (points.length === 0) {
    return issues;
  }

  const uploadCenter = center(turf.featureCollection(points));
  const sitePoint = turf.point([siteLocation.longitude, siteLocation.latitude]);

  const distanceKm = distance(uploadCenter, sitePoint, { units: "kilometers" });

  if (distanceKm > SITE_DISTANCE_WARNING_KM) {
    issues.push({
      severity: "warning",
      category: "data_quality",
      code: "DISTANCE_FROM_SITE",
      message: `Uploaded data is approximately ${distanceKm < 2
        ? distanceKm.toFixed(2)
        : distanceKm.toFixed(1)} km from the site location. Please verify you are uploading to the correct site.`,
    });
  }

  return issues;
}

function validateShapefileCompleteness(
  context: ValidationContext
): ValidationIssue[] {
  const issues: ValidationIssue[] = [];
  const { geometryUploads } = context;

  geometryUploads.forEach((upload) => {
    if (upload.type === "shp") {
      if (!upload.files.shx) {
        issues.push({
          severity: "warning",
          category: "geometry",
          code: "MISSING_SHX",
          message: `Shapefile "${upload.name}" is missing the .shx index file. This may cause processing issues.`,
          fileId: upload.id,
        });
      }
      if (!upload.files.dbf) {
        issues.push({
          severity: "warning",
          category: "geometry",
          code: "MISSING_DBF",
          message: `Shapefile "${upload.name}" is missing the .dbf attribute file. This may cause processing issues.`,
          fileId: upload.id,
        });
      }
    }
  });

  return issues;
}

export function validateOrder(context: ValidationContext): ValidationResult {
  const allIssues = [
    ...validateFiles(context),
    ...validateGeometry(context),
    ...validateForm(context),
    ...validateDataQuality(context),
    ...validateFileAge(context),
    ...validateDistanceFromSite(context),
    ...validateShapefileCompleteness(context),
  ];

  const errors = allIssues.filter((issue) => issue.severity === "error");
  const warnings = allIssues.filter((issue) => issue.severity === "warning");

  return {
    valid: errors.length === 0,
    errors,
    warnings,
  };
}
